import type { VaultProvider } from '../../vaults/fetchVaultsAll'
import type {
  CounterpartyTerms,
  RateKind,
  RateTerms,
  SupplyExitMode,
  SupplyExitTerms,
} from '../types'

/**
 * Per-provider INVARIANTS — the facts that are true of every row a provider
 * emits, and that no row carries a field for.
 *
 * This is the vault analogue of the lender `adapters/` directory, collapsed
 * into a table because vaults differ along far fewer axes than lenders do:
 * there is no borrow side, no liquidation model and no collateral set, so what
 * is left is "how is the rate set, how do you get out, and who is on the other
 * side". Sixteen providers × seven answers fits in one screen and stays
 * auditable; sixteen adapter files would not.
 *
 * `Record<VaultProvider, …>` is deliberate: a new provider is a COMPILE ERROR
 * here, which is the mechanism the plan's §6 enforcement was supposed to
 * provide and never did.
 */
export interface VaultProviderTraits {
  /**
   * How the headline rate is SET. Distinct from where the number was read —
   * `savings` rows are read from an API or the chain but the rate itself is
   * governance-set either way.
   */
  rateKind: RateKind
  /** WHERE the number came from, for the rows where the provider is uniform. */
  rateSource: RateTerms['source']
  /**
   * What period the rate describes. Absent ⇒ `spot`. See `RateTerms.window`:
   * the providers that MEASURE a return rather than quote a rate must say so,
   * or a trailing number lands in the same column as a live offer.
   */
  rateWindow?: NonNullable<RateTerms['window']>
  /**
   * Exit mode when the row does not carry its own `withdrawalMode`.
   *
   * `instant-capped` rather than `instant` for every ERC-4626 vault over
   * lending markets: those vaults hold a fraction of TVL as cash and the rest
   * as supply positions, so a withdrawal larger than the liquid balance simply
   * cannot settle this block. The builder narrows this to `instant` only when
   * the row PROVES full liquidity (`liquidity >= totalAssets`).
   */
  defaultExitMode: SupplyExitMode
  /** Does exiting cost an unknown amount? Vaults redeem at par; PTs do not. */
  priceRisk: SupplyExitTerms['priceRisk']
  counterpartyKind: CounterpartyTerms['kind']
  /**
   * Solvency when it is a STRUCTURAL property of the provider — true of every
   * row it emits, with no curation needed.
   *
   * Absent ⇒ the answer varies per vault (or has no honest answer in this
   * vocabulary) and must come from a curated per-vault classification. The
   * builder then falls back to `overcollateralized` and marks
   * `coverage.pending.counterparty`, so an assumed answer is never mistaken for
   * an assessed one.
   *
   * The line between the two is whether the protocol can be reasoned about
   * without knowing WHICH vault: a MetaMorpho allocator only reaches Morpho
   * Blue markets, and every one of those requires collateral. A Lagoon vault
   * can hold literally anything its curator picks — one of the live ones is
   * institutional credit — so no provider-level answer exists.
   */
  solvency?: CounterpartyTerms['solvency']
  /**
   * Does the provider report a `fee` field, and is it a PERFORMANCE fee on
   * yield? `false` means the provider publishes no fee at all — which must be
   * reported as unknown, never as an empty fee list (an empty list reads as
   * "free", and none of these protocols are).
   */
  reportsPerformanceFee: boolean
  /**
   * `true` when the provider reports `fee` as a FRACTION (`0.1` = 10 %) rather
   * than the percent every other provider uses.
   *
   * Exactly one provider does this — `aave-earn`, and its own type says so —
   * which is precisely why it needs a flag instead of a shared assumption: a
   * uniform `unit: 'percent'` renders a 10 % curator fee as "0.1 %", a 100×
   * understatement of the one number on the sheet the curator is paid.
   */
  feeIsFraction?: boolean
  /** Does the provider emit `timelock` / role fields? */
  reportsGovernance: boolean
  /** Does the provider decompose its backing (`VaultMarketExposure[]`)? */
  reportsExposures: boolean
  /**
   * Is the underlying position a claim on a decomposable set of markets at
   * all? `false` for LSTs, savings wrappers, PTs and trading books — for those
   * an absent `backedBy` is a POSITIVE fact (`coverage.notApplicable`), not a
   * gap we have yet to fill.
   */
  hasDecomposableBacking: boolean
  /**
   * Does the underlying position have a debt accumulator, i.e. is
   * `utilization` a meaningful question here?
   */
  hasUtilization: boolean
  /** Is the principal at risk in the ordinary course (a trading book)? */
  volatilePrincipal?: boolean
}

const LENDING_VAULT: Omit<VaultProviderTraits, 'reportsGovernance'> = {
  rateKind: 'variable-curve',
  rateSource: 'utilization-curve',
  // Structural, not curated: these vaults can only reach markets that require
  // collateral. A shortfall is possible (bad debt socializes to depositors, and
  // `principal.risks` carries that), but the loan book itself is collateralized
  // by construction — which is a different statement from Maple's or Native's.
  solvency: 'overcollateralized',
  defaultExitMode: 'instant-capped',
  priceRisk: 'none',
  counterpartyKind: 'vault-strategy',
  reportsPerformanceFee: true,
  reportsExposures: false,
  hasDecomposableBacking: true,
  hasUtilization: true,
}

export const VAULT_PROVIDER_TRAITS: Record<VaultProvider, VaultProviderTraits> =
  {
    morpho: {
      ...LENDING_VAULT,
      reportsGovernance: true,
      reportsExposures: true,
    },
    // Lista's earn vaults reuse the MetaMorpho shape verbatim (they are typed
    // as `MorphoVaults`), so they inherit `exposures` and the role fields. The
    // `timelock: 0` those rows carry is REAL — Lista runs no timelock pattern —
    // not a missing read, so it must be reported rather than dropped.
    lista: {
      ...LENDING_VAULT,
      reportsGovernance: true,
      reportsExposures: true,
    },
    // Silo's curated vaults ("soTokens") are the MetaMorpho shape on Silo v2/v3
    // markets, and the indexer is unusually complete: `exposures`, the full role
    // set INCLUDING allocators, a real per-market utilization, and incentive
    // programs as their own query root. Two consequences worth stating.
    //
    // `hasUtilization: true` is genuinely satisfiable here — the vault's
    // allocation-weighted `Σ allocation × marketUtilization` fills the block —
    // unlike the sibling curated providers, where it stays permanently pending
    // because no underlying totals are joined.
    //
    // And the rate is a real two-leg split: `userApr` is BASE only
    // (`apr × (1 − performanceFee)`, verified on the whole book), with
    // incentives summed separately into `rewardsRate`. Do not restore the old
    // assumption that Silo folds rewards into its user yield — it does not, and
    // believing it pinned every incentivised vault's rewards leg at zero.
    silo: { ...LENDING_VAULT, reportsGovernance: true, reportsExposures: true },
    // Euler Earn publishes `timelock` + the full role set but NOT `exposures`,
    // contrary to TERM_SHEET_PLAN §7. Its backing is decomposable in principle,
    // so this is `coverage.pending`, not `notApplicable`.
    'euler-earn': { ...LENDING_VAULT, reportsGovernance: true },
    // fTokens are a claim on the Fluid liquidity layer rather than a curated
    // allocation, and the rate is set at that layer, not by this contract.
    fluid: {
      ...LENDING_VAULT,
      counterpartyKind: 'pool',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: true,
    },
    // The only provider that publishes a real debt accumulator on the row
    // (`totalBorrowed` / `expectedLiquidity`), so utilization is exact here.
    // Lends to leveraged credit accounts, which post collateral and carry a
    // health factor — collateralized, like the rest of this class.
    gearbox: { ...LENDING_VAULT, reportsGovernance: false },
    // A thin 4626 wrapper over an Aave v3 supply position: the rate IS the
    // reserve's supply curve. Publishes no liquidity field of its own.
    'aave-earn': {
      ...LENDING_VAULT,
      counterpartyKind: 'pool',
      reportsGovernance: false,
      // The ONLY provider reporting `fee` as a fraction. Its own type documents
      // it (`0.1` = 10 %) while morpho / silo / euler-earn / termmax / yearn all
      // document percent.
      feeIsFraction: true,
    },
    // The VAULT is perpetual and its rate floats with what the curator earns
    // rolling FT positions — it is NOT itself a fixed-term instrument, which
    // the earn normalizer used to claim (a locked rate with no maturity).
    termmax: {
      ...LENDING_VAULT,
      rateKind: 'variable-managed',
      rateSource: 'derived',
      reportsGovernance: true,
    },
    savings: {
      rateKind: 'variable-managed',
      rateSource: 'governance',
      defaultExitMode: 'instant',
      priceRisk: 'none',
      counterpartyKind: 'vault-strategy',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: false,
      hasUtilization: false,
    },
    lst: {
      rateKind: 'variable-managed',
      // A pro-rata claim on real staked capital — fully backed, no borrower.
      // The residual risk is slashing and the exit queue, both of which the
      // exit terms and the profile state; neither is a solvency question.
      solvency: 'overcollateralized',
      // Staking rewards are neither a curve nor a governance dial — the rate is
      // reconstructed from the share/asset ratio moving.
      rateSource: 'derived',
      defaultExitMode: 'queued',
      priceRisk: 'none',
      counterpartyKind: 'pool',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: false,
      hasUtilization: false,
    },
    // A PT's rate is locked at purchase and realized by holding to maturity —
    // the TermMax FT promise reached by an AMM discount instead of a quote.
    pendle: {
      rateKind: 'fixed-term',
      // A PT redeems 1:1 for the accounting asset at maturity — there is no
      // borrower, no leverage and no tranching on the Pendle side, so the
      // fixed-rate promise itself carries no credit risk.
      //
      // What it does NOT mean: the SY it redeems into is only as good as the
      // protocol behind it, and that risk passes straight through to the PT
      // holder. That is a property of the underlying, stated in the profile's
      // implications, not a defect in this classification.
      solvency: 'overcollateralized',
      rateSource: 'derived',
      rateWindow: { kind: 'forward' },
      defaultExitMode: 'market-sale',
      priceRisk: 'market-price',
      counterpartyKind: 'p2p',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: false,
      hasUtilization: false,
    },
    // Spectra PTs are the same instrument as Pendle's, so the traits agree
    // where the economics agree — with one deliberate divergence below.
    spectra: {
      rateKind: 'fixed-term',
      // Same call as Pendle, and for the same reason: no borrower, no
      // leverage, no tranching on the Spectra side, so the fixed-rate promise
      // itself carries no credit risk. The IBT it redeems into is only as good
      // as the protocol behind it, and that passes straight through to the PT
      // holder — a property of the underlying, stated in the profile.
      //
      // **The divergence from Pendle is that Spectra makes the failure mode
      // legible.** `ptRate` starts at par and is written DOWN whenever the IBT
      // loses value ("The `ptRate` can only decrease" — their own docs work the
      // example where a halved IBT returns half the deposit). Pendle carries
      // the identical exposure through its SY and publishes no equivalent
      // number. So this is not a weaker claim than Pendle's, it is the same
      // claim with a live check attached: the row ships `ptRate` and
      // `principalWriteDownBps`, and a market written down to zero is dropped
      // outright rather than served as a bond.
      solvency: 'overcollateralized',
      rateSource: 'derived',
      rateWindow: { kind: 'forward' },
      defaultExitMode: 'market-sale',
      priceRisk: 'market-price',
      counterpartyKind: 'p2p',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: false,
      hasUtilization: false,
    },
    lagoon: {
      rateKind: 'variable-managed',
      rateSource: 'api',
      // Overwritten per row from `aprWindow`; the default is the honest floor.
      rateWindow: { kind: 'trailing' },
      defaultExitMode: 'request-based',
      priceRisk: 'none',
      counterpartyKind: 'vault-strategy',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: true,
      hasUtilization: false,
    },
    upshift: {
      rateKind: 'variable-managed',
      rateSource: 'api',
      rateWindow: { kind: 'trailing' },
      // Every vault requests → settles → claims; the per-vault delay runs 0 to
      // 30 days and rides on the row as `withdrawalCooldownSeconds`. One vault
      // (Upshift Clear RWA) declares `fee-or-queued` for its instant leg.
      defaultExitMode: 'request-based',
      priceRisk: 'none',
      counterpartyKind: 'vault-strategy',
      // Upshift publishes a full schedule — performance AND management, per
      // vault, with waiver flags. Reported `false` while the fetcher dropped
      // the block, which made every row's empty fee list read as "free" when
      // one vault charges 20 % + 2 %.
      reportsPerformanceFee: true,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: true,
      hasUtilization: false,
    },
    yearn: {
      rateKind: 'variable-managed',
      rateSource: 'api',
      // Refined per row from `isForwardApr` — Yearn publishes BOTH kinds under
      // one field name, so the flag is the only thing distinguishing a
      // projection from a measurement.
      rateWindow: { kind: 'trailing' },
      defaultExitMode: 'instant-capped',
      priceRisk: 'none',
      counterpartyKind: 'vault-strategy',
      reportsPerformanceFee: true,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: true,
      hasUtilization: false,
    },
    // GM/GLV baskets: the "rate" is a realized trading outcome over a past
    // window, which is why `rateKind: 'none'` — a `variable-*` kind would imply
    // a curve that does not exist, and the number is not a promise at all.
    gmx: {
      rateKind: 'realized',
      rateSource: 'api',
      rateWindow: { kind: 'trailing' },
      defaultExitMode: 'request-based',
      priceRisk: 'none',
      counterpartyKind: 'pool',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: false,
      hasUtilization: false,
      volatilePrincipal: true,
    },
    hypercore: {
      rateKind: 'realized',
      rateSource: 'api',
      rateWindow: { kind: 'trailing' },
      defaultExitMode: 'queued',
      priceRisk: 'none',
      counterpartyKind: 'vault-strategy',
      reportsPerformanceFee: false,
      reportsGovernance: false,
      reportsExposures: false,
      hasDecomposableBacking: false,
      hasUtilization: false,
      volatilePrincipal: true,
    },
  }

export function vaultTraits(provider: string): VaultProviderTraits | undefined {
  return VAULT_PROVIDER_TRAITS[provider as VaultProvider]
}

/**
 * `LagoonVault.aprWindow` → a `RateTerms.window`.
 *
 * `'inception'` keeps its label rather than being mapped to a duration: it is
 * "since this vault existed", which is not a fixed number of seconds and would
 * be a lie as one.
 */
export function windowFromAprWindow(
  aprWindow: string | undefined,
): NonNullable<RateTerms['window']> | undefined {
  switch (aprWindow) {
    case 'weekly':
      return { kind: 'trailing', secs: 604_800, label: 'weekly' }
    case 'monthly':
      return { kind: 'trailing', secs: 2_592_000, label: 'monthly' }
    case 'yearly':
      return { kind: 'trailing', secs: 31_536_000, label: 'yearly' }
    case 'inception':
      return { kind: 'trailing', label: 'since inception' }
    case 'none':
      return undefined
    default:
      return undefined
  }
}
